import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { useGetReceiptById } from "../../hooks/api/useReceipt";
import { useGetInvoiceById } from "../../hooks/api/useInvoice";
import { useGetOrder } from "../../hooks/api/useSalesOrder";
import { useGetProfile } from "../../hooks/api/useProfile";
import type { Order } from "../Orders/types/salesOrder";
import type { Invoice } from "../Invoice/types/invoice";
import {
  KivuI18nContext,
  PAGE_PORTAL_ORDERS_INVOICE_DETAIL,
  PAGE_PORTAL_ORDERS_PAYMENT_DETAIL,
  PAGE_PORTAL_ORDERS_SALES_DETAIL,
  PORTAL_PREFIX,
  localizedPath,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import PrintButton from "../common/PrintButton/PrintButton";
import PrintableContent from "../common/PrintableContent/PrintableContent";
import CommonLoader from "../common/CommonLoader/CommonLoader";
import { useReportError } from "../../hooks/useReportError";
import ReceiptDetailPrintableBody from "./ReceiptDetailPrintableBody";
import { ReceiptStatus } from "./types/receipt";

export interface ReceiptDetailModalProps {
  open: boolean;
  receiptId: string | null;
  onClose: () => void;
}

const receiptTitleChipColor = (
  status: ReceiptStatus
): "success" | "error" | "default" => {
  switch (status) {
    case ReceiptStatus.ACTIVE:
      return "success";
    case ReceiptStatus.VOID:
      return "error";
    default:
      return "default";
  }
};

const ReceiptDetailModal: React.FC<ReceiptDetailModalProps> = ({
  open,
  receiptId,
  onClose
}) => {
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const reportError = useReportError();

  const { receiptDetail, receiptList } = useSelector(
    (state: RootState) => state.receipts
  );
  const { invoiceDetail } = useSelector((state: RootState) => state.invoices);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  const { mutateAsync: fetchReceiptById, isPending } = useGetReceiptById();
  const getProfile = useGetProfile();
  const { mutateAsync: fetchInvoiceById } = useGetInvoiceById();
  const { mutateAsync: fetchOrderById } = useGetOrder();
  const printContentRef = useRef<HTMLDivElement>(null);
  const [orderForPrint, setOrderForPrint] = useState<Order | null>(null);
  const [linkedInvoice, setLinkedInvoice] = useState<Invoice | null>(null);

  useEffect(() => {
    if (!open || !receiptId) return;
    (async () => {
      try {
        await fetchReceiptById({ receiptId });
      } catch (err) {
        reportError("Failed to fetch receipt detail (UI)", err, {
          component: "ReceiptDetailModal",
          receiptId: receiptId ?? ""
        });
      }
    })();
  }, [open, receiptId, fetchReceiptById, reportError]);

  useEffect(() => {
    if (!open || userDetails) return;
    void getProfile.mutateAsync().catch(() => {});
  }, [open, userDetails, getProfile.mutateAsync]);

  const receipt = useMemo(() => {
    if (!receiptId) return undefined;
    if (receiptDetail?.receiptId === receiptId) return receiptDetail;
    return receiptList.find((r) => r.receiptId === receiptId);
  }, [receiptDetail, receiptId, receiptList]);

  const receiptMatches =
    !!receipt && receiptId !== null && receipt.receiptId === receiptId;

  useEffect(() => {
    if (!open || !receipt?.invoiceId) {
      setLinkedInvoice(null);
      return;
    }
    const invoiceId = receipt.invoiceId;
    let cancelled = false;
    setLinkedInvoice(null);
    (async () => {
      try {
        const inv = await fetchInvoiceById({ invoiceId });
        if (!cancelled && inv?.invoiceId === invoiceId) {
          setLinkedInvoice(inv);
        }
      } catch (err) {
        reportError("Failed to fetch invoice detail from receipt modal", err, {
          component: "ReceiptDetailModal",
          invoiceId
        });
        if (!cancelled) setLinkedInvoice(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, receipt?.invoiceId, fetchInvoiceById, reportError]);

  useEffect(() => {
    if (!open || !receipt?.orderId) {
      setOrderForPrint(null);
      return;
    }
    let cancelled = false;
    setOrderForPrint(null);
    (async () => {
      try {
        const order = await fetchOrderById({ orderId: receipt.orderId });
        if (!cancelled) setOrderForPrint(order);
      } catch (err) {
        reportError("Failed to fetch order for receipt print (modal)", err, {
          component: "ReceiptDetailModal",
          orderId: receipt.orderId
        });
        if (!cancelled) setOrderForPrint(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, receipt?.orderId, fetchOrderById, reportError]);

  const invoiceDetailPath = receipt
    ? localizedPath(
        `/${PORTAL_PREFIX}/${PAGE_PORTAL_ORDERS_INVOICE_DETAIL.replace(
          ":invoiceId",
          receipt.invoiceId
        )}`,
        language,
        defaultLang
      )
    : "";

  const paymentDetailPath = receipt
    ? localizedPath(
        `/${PORTAL_PREFIX}/${PAGE_PORTAL_ORDERS_PAYMENT_DETAIL.replace(
          ":paymentId",
          receipt.paymentId
        )}`,
        language,
        defaultLang
      )
    : "";

  const salesOrderDetailPath = receipt
    ? localizedPath(
        `/${PORTAL_PREFIX}/${PAGE_PORTAL_ORDERS_SALES_DETAIL.replace(
          ":orderId",
          receipt.orderId
        )}`,
        language,
        defaultLang
      )
    : "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      aria-labelledby="receipt-detail-modal-title"
    >
      <DialogTitle
        id="receipt-detail-modal-title"
        aria-label={t("receipt-details-title", "Receipt details")}
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          pr: 1,
          pb: 1
        }}
      >
        <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
          <Typography variant="h6" component="span" fontWeight="bold">
            {t("receipt-details-title", "Receipt details")}
          </Typography>
          {receipt && (
            <Chip
              size="small"
              sx={{ ml: 1, verticalAlign: "middle" }}
              label={receipt.status}
              color={receiptTitleChipColor(receipt.status)}
            />
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={1} flexShrink={0}>
          {receipt && (
            <PrintButton
              targetRef={printContentRef}
              title={`${t("receipt-detail-print-title", "Receipt")}: ${receipt.receiptId}`}
              label={t("receipt-detail-print-label", "Print Receipt")}
            />
          )}
          <IconButton
            aria-label={t("close", "Close")}
            onClick={onClose}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 0 }}>
        {open && receiptId && isPending && (
          <CommonLoader
            loadingMessage={t(
              "receipt-detail.fetching",
              "Fetching receipt details..."
            )}
          />
        )}

        {open && receiptId && !isPending && !receiptMatches && (
          <Typography color="text.secondary">
            {t("receipt-not-found", "Receipt could not be loaded.")}
          </Typography>
        )}

        {open && receiptId && !isPending && receiptMatches && receipt && (
          <PrintableContent
            id="receipt-printable-content-modal"
            ref={printContentRef}
          >
            <ReceiptDetailPrintableBody
              receipt={receipt}
              invoiceDetail={invoiceDetail}
              invoiceLinked={linkedInvoice}
              orderForPrint={orderForPrint}
              invoiceDetailPath={invoiceDetailPath}
              paymentDetailPath={paymentDetailPath}
              salesOrderDetailPath={salesOrderDetailPath}
              onBeforeEntityNavigate={onClose}
              t={t}
            />
          </PrintableContent>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReceiptDetailModal;
